"use client";

import React, { useState } from "react";
import { useToast } from "./Toast";

interface EmailSequenceGeneratorProps {
  className?: string;
}

export default function EmailSequenceGenerator({ className = "" }: EmailSequenceGeneratorProps) {
  const [product, setProduct] = useState("");
  const [audience, setAudience] = useState("");
  const [sequenceType, setSequenceType] = useState("VENDA");
  const [goal, setGoal] = useState("");
  const [tone, setTone] = useState("AMIGÁVEL");
  const [emailCount, setEmailCount] = useState("5");
  const [sequence, setSequence] = useState<any>(null);
  const [isLoading, setIsLoading] = useState(false);
  const { addToast } = useToast();

  const generateSequence = async () => {
    if (!product || !audience || !goal) {
      addToast("⚠️ Preencha todos os campos obrigatórios", "error");
      return;
    }

    setIsLoading(true);
    try {
      const response = await fetch("/api/email-sequence", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ 
          product, 
          audience, 
          sequenceType, 
          goal, 
          tone, 
          emailCount: parseInt(emailCount)
        }),
      });

      if (response.ok) {
        const data = await response.json();
        setSequence(data);
        addToast("📧 Sequência de emails gerada com sucesso!", "success");
      } else {
        const error = await response.json();
        addToast(error.error || "Erro ao gerar sequência", "error");
      }
    } catch (error) {
      console.error("Erro:", error);
      addToast("Erro ao gerar sequência", "error");
    } finally {
      setIsLoading(false);
    }
  };

  const copyEmailContent = (content: string) => {
    navigator.clipboard.writeText(content);
    addToast("📋 Email copiado!", "success");
  };

  return (
    <div className={`bg-gradient-to-br from-purple-900 via-blue-900 to-indigo-900 text-white rounded-lg p-6 border border-purple-500/30 font-mono ${className}`}>
      <div className="mb-6">
        <h3 className="text-2xl font-bold mb-2 text-purple-300">
          📧 EMAIL SEQUENCE GENERATOR 📧
        </h3>
        <div className="text-xs text-purple-400 mb-4">
          [EMAIL_AUTOMATION] v2.0.0 | STATUS: ACTIVE | AUTH: MARKETING_PRO
        </div>
      </div>
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
        <div>
          <label className="block text-xs text-purple-400 mb-2">PRODUCT_NAME:</label>
          <input
            type="text"
            value={product}
            onChange={(e) => setProduct(e.target.value)}
            className="w-full bg-purple-950/50 border border-purple-500/50 text-purple-300 px-3 py-2 rounded focus:outline-none focus:border-purple-400 focus:shadow-[0_0_10px_rgba(168,85,247,0.3)]"
            placeholder="ex: Curso de Marketing Digital"
          />
        </div>

        <div>
          <label className="block text-xs text-purple-400 mb-2">TARGET_AUDIENCE:</label>
          <input
            type="text"
            value={audience}
            onChange={(e) => setAudience(e.target.value)}
            className="w-full bg-purple-950/50 border border-purple-500/50 text-purple-300 px-3 py-2 rounded focus:outline-none focus:border-purple-400"
            placeholder="ex: Empreendedores iniciantes"
          />
        </div>

        <div>
          <label className="block text-xs text-purple-400 mb-2">SEQUENCE_TYPE:</label>
          <select
            value={sequenceType}
            onChange={(e) => setSequenceType(e.target.value)}
            className="w-full bg-purple-950/50 border border-purple-500/50 text-purple-300 px-3 py-2 rounded focus:outline-none focus:border-purple-400"
          >
            <option value="VENDA">VENDA_DIRETA</option>
            <option value="NUTRIÇÃO">NUTRIÇÃO</option>
            <option value="LANÇAMENTO">LANÇAMENTO</option>
            <option value="CART_ABANDONO">CART_ABANDONO</option>
            <option value="REATIVAÇÃO">REATIVAÇÃO</option>
          </select>
        </div>

        <div>
          <label className="block text-xs text-purple-400 mb-2">CAMPAIGN_GOAL:</label>
          <input
            type="text"
            value={goal}
            onChange={(e) => setGoal(e.target.value)}
            className="w-full bg-purple-950/50 border border-purple-500/50 text-purple-300 px-3 py-2 rounded focus:outline-none focus:border-purple-400"
            placeholder="ex: Vender curso com 20% de conversão"
          />
        </div>

        <div>
          <label className="block text-xs text-purple-400 mb-2">EMAIL_TONE:</label>
          <select
            value={tone}
            onChange={(e) => setTone(e.target.value)}
            className="w-full bg-purple-950/50 border border-purple-500/50 text-purple-300 px-3 py-2 rounded focus:outline-none focus:border-purple-400"
          >
            <option value="AMIGÁVEL">FRIENDLY</option>
            <option value="PROFISSIONAL">PROFESSIONAL</option>
            <option value="URGENTE">URGENT</option>
            <option value="EXCLUSIVO">EXCLUSIVE</option>
            <option value="EDUCATIVO">EDUCATIONAL</option>
          </select>
        </div>

        <div>
          <label className="block text-xs text-purple-400 mb-2">EMAIL_COUNT:</label>
          <select
            value={emailCount}
            onChange={(e) => setEmailCount(e.target.value)}
            className="w-full bg-purple-950/50 border border-purple-500/50 text-purple-300 px-3 py-2 rounded focus:outline-none focus:border-purple-400"
          >
            <option value="3">3_EMAILS</option>
            <option value="5">5_EMAILS</option>
            <option value="7">7_EMAILS</option>
            <option value="10">10_EMAILS</option>
          </select>
        </div>
      </div>

      <button
        onClick={generateSequence}
        disabled={isLoading || !product || !audience || !goal}
        className="w-full bg-gradient-to-r from-purple-600 to-pink-600 text-white py-3 px-6 rounded font-mono text-sm hover:from-purple-700 hover:to-pink-700 disabled:from-gray-600 disabled:to-gray-600 transition-all duration-300 mb-6"
      >
        {isLoading ? (
          <span className="flex items-center justify-center">
            <span className="animate-pulse">⚡</span> GENERATING_SEQUENCE...
          </span>
        ) : (
          <span className="flex items-center justify-center">
            📧 [GENERATE_EMAILS] 📧
          </span>
        )}
      </button>

      {sequence && (
        <div className="mt-6 space-y-4">
          <div className="text-xs text-purple-400 mb-4">
            [SEQUENCE_GENERATED] | EMAILS: {sequence.total_emails} | CONVERSION: {sequence.estimated_conversion}
          </div>
          
          {sequence.emails?.map((email: any, index: number) => (
            <div key={index} className="bg-purple-950/30 border border-purple-500/30 rounded p-4 mb-4">
              <div className="flex justify-between items-center mb-3">
                <h4 className="text-lg font-bold text-purple-300">
                  EMAIL #{email.day} - {email.subject}
                </h4>
                <button
                  onClick={() => copyEmailContent(JSON.stringify(email, null, 2))}
                  className="text-xs bg-purple-500/20 px-2 py-1 rounded hover:bg-purple-500/30"
                >
                  [COPY_EMAIL]
                </button>
              </div>
              
              <div className="mb-3">
                <h5 className="text-sm font-bold text-purple-400 mb-2">PREVIEW_TEXT:</h5>
                <div className="text-purple-300 text-sm italic">{email.preview_text}</div>
              </div>

              <div className="mb-3">
                <h5 className="text-sm font-bold text-purple-400 mb-2">OPENING:</h5>
                <div className="text-purple-300 text-sm bg-black/30 p-2 rounded">{email.body?.opening}</div>
              </div>

              <div className="mb-3">
                <h5 className="text-sm font-bold text-purple-400 mb-2">STORY:</h5>
                <div className="text-purple-300 text-sm bg-black/30 p-2 rounded">{email.body?.story}</div>
              </div>

              <div className="mb-3">
                <h5 className="text-sm font-bold text-purple-400 mb-2">SOLUTION:</h5>
                <div className="text-purple-300 text-sm bg-black/30 p-2 rounded">{email.body?.solution}</div>
              </div>

              <div className="mb-3">
                <h5 className="text-sm font-bold text-purple-400 mb-2">BENEFITS:</h5>
                <div className="space-y-1">
                  {email.body?.benefits?.map((benefit: string, bIndex: number) => (
                    <div key={bIndex} className="text-purple-300 text-sm">• {benefit}</div>
                  ))}
                </div>
              </div>

              <div className="mb-3">
                <h5 className="text-sm font-bold text-purple-400 mb-2">SOCIAL_PROOF:</h5>
                <div className="text-purple-300 text-sm bg-black/30 p-2 rounded">{email.body?.social_proof}</div>
              </div>

              <div className="mb-3">
                <h5 className="text-sm font-bold text-purple-400 mb-2">URGENCY:</h5>
                <div className="text-purple-300 text-sm bg-black/30 p-2 rounded">{email.body?.urgency}</div>
              </div>

              <div className="mb-3">
                <h5 className="text-sm font-bold text-purple-400 mb-2">CTA:</h5>
                <div className="space-y-1">
                  <div className="text-purple-300 text-sm font-bold">PRIMARY: {email.body?.cta?.primary}</div>
                  <div className="text-purple-300 text-sm">SECONDARY: {email.body?.cta?.secondary}</div>
                  <div className="text-purple-300 text-sm italic">P.S.: {email.body?.cta?.ps}</div>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-xs">
                <div className="bg-purple-900/30 p-2 rounded">
                  <span className="text-purple-400">SEND_TIME:</span>
                  <span className="text-purple-300 ml-2">{email.send_time}</span>
                </div>
                <div className="bg-purple-900/30 p-2 rounded">
                  <span className="text-purple-400">OPEN_RATE_GOAL:</span>
                  <span className="text-purple-300 ml-2">{email.metrics?.open_rate_goal}</span>
                </div>
              </div>
            </div>
          ))}

          <div className="bg-purple-900/20 border border-purple-500/30 rounded p-4">
            <h4 className="text-lg font-bold text-purple-300 mb-3">🤖 AUTOMATION_RULES:</h4>
            <div className="space-y-1">
              {sequence.automation_rules?.map((rule: string, index: number) => (
                <div key={index} className="text-purple-300 text-sm">• {rule}</div>
              ))}
            </div>
          </div>

          <div className="text-xs text-purple-400 mt-4">
            [SEQUENCE_COMPLETE] | ESTIMATED_REVENUE: R$ {sequence.metadata?.estimatedRevenue?.toLocaleString()} | AUTH: ADMIN
          </div>
        </div>
      )}
    </div>
  );
}
